import React, { forwardRef, useState, useEffect, useCallback } from 'react'
import { useDispatch } from 'react-redux'

import PropTypes from 'prop-types'

import filter from 'lodash/filter'
import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'
import toLower from 'lodash/toLower'

import { useT } from '@galo-monitor/i18n'
import useGaloMonitorApi from '@galo-monitor/services/hooks/useGaloMonitorApi'
import { useSnackbar } from '@galo-monitor/snackbar'
import { RoleActions } from '@galo-monitor/stores/role'
import CardAccordion from '@galo-monitor/web-components/CardAccordion'
import Form from '@galo-monitor/web-components/Form'

import ModuleFunctionalities from './ModuleFunctionalities'

const RoleModulesForm = forwardRef((props, formRef) => {
  const {
    role,
    loading,
    setLoading,
    onSuccess,
    onError
  } = props

  const dispatch = useCallback(useDispatch(), [])
  const t = useT()
  const snackbar = useSnackbar()

  const { data: modules, isValidating: isValidatingModules } = useGaloMonitorApi('/companies/farms/roles/modules')
  const {
    data: currentRoleModules,
    error: currentRoleError,
    isValidating: isValidatingCurrentRoleModules
  } = useGaloMonitorApi(`/companies/farms/roles/modules/${ role.id }`)

  const [functionalityPermissions, setFunctionalityPermissions] = useState({})

  const handleSubmit = useCallback(
    () => {
      setLoading(true)

      const functionalities = map(Object.keys(functionalityPermissions), key => ({
        id: +key,
        reader: functionalityPermissions[key].read,
        created: functionalityPermissions[key].write,
        edited: functionalityPermissions[key].edit,
        deleted: functionalityPermissions[key].delete
      }))

      dispatch(RoleActions.saveFarmRoleModules(
        {
          ...role,
          functionalities
        },
        () => {
          snackbar.success(t('{this} {action} successfully!', {
            this: t('role', { howMany: 1 }),
            action: toLower(t(isEmpty(role) ? 'created' : 'updated', { gender: 'male', howMany: 1 }))
          }))
          onSuccess()
        },
        onError
      ))
    },
    [dispatch, functionalityPermissions, onError, onSuccess, role, setLoading, snackbar, t]
  )

  useEffect(() => {
    if (!isEmpty(currentRoleError))
      onError(currentRoleError)
  }, [currentRoleError, onError])

  useEffect(() => role.id && setLoading(true), [role.id, setLoading])

  useEffect(() => {
    if (!isEmpty(currentRoleModules))
      setLoading(false)
  }, [currentRoleModules, setLoading])

  return (
    <Form
      ref={ formRef }
      onSubmit={ handleSubmit }
    >
      {map(modules, mod => (
        <CardAccordion
          key={ mod.id }
          title={ mod.name }
          defaultOpened
        >
          <ModuleFunctionalities
            isLoading={ loading || isValidatingModules || isValidatingCurrentRoleModules }
            functionalities={ mod.functionalities }
            permissions={
              !isEmpty(currentRoleModules)
                ? filter(currentRoleModules.functionalities, func => func.moduleName === mod.name)
                : []
            }
            setPermissions={ setFunctionalityPermissions }
          />
        </CardAccordion>
      ))}
    </Form>
  )
})

RoleModulesForm.propTypes = {
  role: PropTypes.object,
  loading: PropTypes.bool,
  setLoading: PropTypes.func,
  onSuccess: PropTypes.func,
  onError: PropTypes.func
}

RoleModulesForm.defaultProps = {
  role: {},
  loading: false,
  setLoading: () => {},
  onSuccess: () => {},
  onError: () => {}
}

export default RoleModulesForm
